import { LoadingIndicator } from '@vrplatform/components-legacy';
import { NumberInput, type NumberInputProps, Text } from '@mantine/core';
import { forwardRef, useCallback, useRef, useEffect } from 'react';

interface Props extends NumberInputProps {
  loadingQuery?: boolean;
  loadingMutation?: boolean;
  debounced?: number;
}

export const InputPercentage = forwardRef<HTMLInputElement, Props>(
  (
    {
      value,
      onChange,
      radius,
      placeholder,
      decimalScale = 2,
      styles,
      loadingMutation,
      loadingQuery,
      leftSection: icon,
      debounced,
      ...props
    },
    ref
  ) => {
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const debouncedOnChange = useCallback(
      (newValue: string | number) => {
        if (!debounced) {
          onChange?.(newValue);
          return;
        }

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          onChange?.(newValue);
        }, debounced);
      },
      [onChange, debounced]
    );

    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    return (
      <NumberInput
        ref={ref}
        value={loadingQuery ? '' : value}
        onChange={debouncedOnChange}
        sx={{ height: '100%' }}
        radius={radius}
        placeholder={placeholder}
        inputMode="decimal"
        decimalScale={decimalScale}
        thousandSeparator
        max={100}
        leftSection={
          loadingQuery ? <LoadingIndicator size="xs" /> : icon || <Text>%</Text>
        }
        rightSection={loadingMutation && <LoadingIndicator size="xs" />}
        styles={(...themeProps) => {
          const theme = themeProps[0];

          return {
            root: {
              height: '100%',
            },
            input: {
              height: '100%',
              '&:focus': {
                boxShadow: `0px 0px 0px 2px ${
                  theme.colors[theme.primaryColor][4]
                }40`,
              },
            },
            wrapper: {
              height: '100%',
            },
            ...(typeof styles === 'function' ? styles(...themeProps) : styles),
          };
        }}
        {...props}
      />
    );
  }
);
